// frontend/src/Layouts/Layout.tsx
import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  People as PeopleIcon,
  AccountCircle,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Archive as ArchiveIcon,
  Insights as InsightsIcon,
  AccessTime as AccessTimeIcon,
  Event as EventIcon,
  Settings as SettingsIcon,
  Campaign as CampaignIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useComuneConfig } from '../contexts/ConfigContext';

const drawerWidth = 240;

// Percorsi (file in frontend/public/logos)
const logoPai   = '/logos/logo_EDIH-PAI.jpg';
const logoEu    = '/logos/eu.jpg';
const logoMimit = '/logos/repubblica_italiana.png';

const Layout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { user, logout } = useAuth();
  const comune = useComuneConfig();
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleMenu = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleProfile = () => {
    handleClose();
    navigate('/profile');
  };

  const menuItems = [
    { text: 'Home', icon: <HomeIcon />, path: '/', roles: ['USER', 'ADMIN'], enabled: true },
    { text: 'Utenti', icon: <PeopleIcon />, path: '/users', roles: ['USER', 'ADMIN'], enabled: true },
    { text: 'Chiamate', icon: <PhoneIcon />, path: '/chiamate', roles: ['USER', 'ADMIN'], enabled: true },
    { text: 'Contenuti', icon: <ArchiveIcon />, path: '/archivio', roles: ['USER', 'ADMIN'], enabled: comune.features.archive },
    { text: 'KPI Chiamate', icon: <InsightsIcon />, path: '/kpi', roles: ['USER', 'ADMIN'], enabled: true },
    { text: 'Orari Uffici', icon: <AccessTimeIcon />, path: '/orari', roles: ['USER', 'ADMIN'], enabled: comune.features.officeHours },
    { text: 'Orari Trasferimento', icon: <AccessTimeIcon />, path: '/orari-trasferimento', roles: ['USER', 'ADMIN'], enabled: comune.features.transferHours },
    { text: 'Eventi', icon: <EventIcon />, path: '/eventi', roles: ['USER', 'ADMIN'], enabled: comune.features.events },
    { text: 'Avvisi', icon: <CampaignIcon />, path: '/announcements', roles: ['ADMIN'], enabled: true },
    { text: 'Impostazioni', icon: <SettingsIcon />, path: '/settings', roles: ['ADMIN'], enabled: true },
  ];

  const drawer = (
    <div>
      <Toolbar />
      <Divider />
      <List>
        {menuItems
          .filter((item) => item.enabled && item.roles.includes(user?.role ?? ''))
          .map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => {
                  navigate(item.path);
                  setMobileOpen(false);
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />

      {/* TOP APP BAR */}
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
            aria-label="apri navigazione"
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" noWrap sx={{ flexGrow: 1 }}>
            {comune.displayName}
          </Typography>

          <div>
            <IconButton
              size="large"
              aria-label="account utente"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
            >
              <AccountCircle />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem disabled>
                <Typography variant="body2" color="text.secondary">
                  {user?.email}
                </Typography>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleProfile}>
                <ListItemIcon>
                  <PersonIcon fontSize="small" />
                </ListItemIcon>
                Profilo
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                Esci
              </MenuItem>
            </Menu>
          </div>
        </Toolbar>
      </AppBar>

      {/* LEFT NAV */}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="navigazione principale"
      >
        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* MAIN (padding-bottom per spazio footer fisso) */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          pb: 10,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>

      {/* FIXED FOOTER BAR — 3 colonne equidistanti */}
      <Box
        component="footer"
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          width: { xs: '100%', sm: `calc(100% - ${drawerWidth}px)` },
          ml: { xs: 0, sm: `${drawerWidth}px` },
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          boxShadow: 3,
          py: 1.5,
          px: { xs: 2, sm: 3 },
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            placeItems: 'center',
            gap: { xs: 2, sm: 3, md: 4 },
          }}
        >
          <Box
            component="img"
            src={logoPai}
            alt="EDIH PAI"
            sx={{ height: { xs: 44, sm: 48, md: 52 }, objectFit: 'contain' }}
          />
          <Box
            component="img"
            src={logoEu}
            alt="Finanziato dall’Unione europea"
            sx={{ height: { xs: 42, sm: 46, md: 50 }, objectFit: 'contain' }}
          />
          <Box
            component="img"
            src={logoMimit}
            alt="Ministero delle Imprese e del Made in Italy"
            sx={{ height: { xs: 40, sm: 44, md: 48 }, objectFit: 'contain' }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
